import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';

import { FILTER_KEY } from 'src/constants/keySession';
import { PermissionGroup } from 'src/constants/modulePermission';
import { SECTION } from 'src/constants/section';
import { getModulePath } from 'src/helper/helper';
import history from 'src/services/routing/history';
import { routePaths } from 'src/services/routing/routePaths';
import { useSection } from 'src/components/app/context/SectionContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';

import { styles } from './index.styles';

interface ButtonListProps {
  setIsOpen: (value: boolean) => void;
}

/**
 * ButtonList used in the DrawerNavigation component.
 * It renders a list of navigation buttons based on user permissions.
 * It also includes an admin button if the user has admin privileges.
 *
 */
const ButtonList: FC<ButtonListProps> = ({ setIsOpen }) => {
  const { t } = useTranslation('common');
  const { moduleId, section } = useSection();

  const { getPermissionsForGroup, activeUserPermissionsInCurrentSurvey } =
    useUserPermissions();

  const hasAdminPermissions = useMemo(
    () => getPermissionsForGroup(PermissionGroup.ADMIN).length > 0,
    [getPermissionsForGroup],
  );

  return (
    <Box className="DrawerNavigation-buttonList" sx={styles.buttonList}>
      {activeUserPermissionsInCurrentSurvey.map((permission) => {
        const isActive = moduleId === permission.module.id;
        const route = getModulePath(permission.module.module_type);
        const params =
          route === routePaths.DASHBOARD_DETAIL
            ? {
                slug: permission.module.slug || undefined,
              }
            : undefined;
        const path = generatePath(route, params);

        // Clear filter settings from sessionStorage if we navigate away from userAdmin
        // find more information about why we need to handle filter settings like this in UserListPage.
        if (permission.module.module_type !== SECTION.USER_ADMIN) {
          sessionStorage.removeItem(FILTER_KEY);
        }

        return (
          <Button
            color="inherit"
            fullWidth
            key={permission.module.id}
            onClick={() => {
              setIsOpen(false);
              history.push(path);
            }}
            sx={[styles.button, isActive && styles.activeButton]}
            variant="text"
          >
            <Typography variant="h4">{permission.module.name}</Typography>
          </Button>
        );
      })}
      {hasAdminPermissions && (
        <Button
          color="inherit"
          fullWidth
          onClick={() => {
            setIsOpen(false);
            history.push(routePaths.USER_ADMIN_LIST);
          }}
          sx={[
            styles.button,
            section === SECTION.USER_ADMIN && styles.activeButton,
          ]}
          variant="text"
        >
          <Typography variant="h4">{t('userAdmin')}</Typography>
        </Button>
      )}
    </Box>
  );
};

export default ButtonList;
